import { Coupon } from '@models/membership/Coupon'
import { Currency } from '@models/Currency'
import { MembershipProfile } from '@models/membership/MembershipProfile'
import { Plan } from '@models/membership/Plan'
import { SubscriptionPayment } from '@models/membership/SubscriptionPayment'
import { SBModelUUID } from '@supplement-bacon/react-toolkit/models'

/** Lite, denormalized member embed - not the full `MembershipProfile` model. */
interface MembershipTransactionMember {
  id: number
  firstName: string
  lastName: string
  email: string
  avatar: string | null
}

/** Lite, denormalized plan embed - not the full `Plan` model. */
interface MembershipTransactionPlan {
  id: number
  name: string
  avatar: string | null
  /**
   * Currency the row's amounts are expressed in - the plan's program currency,
   * which the endpoint denormalizes onto the plan. Only the ISO code and the
   * symbol are contractual, so the embed stays a `Pick`, not a `Currency`.
   * `null` when the program has none on file: the row then falls back to the
   * default euro rendering.
   */
  currency: Pick<Currency, 'isoCode' | 'symbol'> | null
}

/** Backend returns capitalized values (SubscriptionPayment::STATUS_* constants). */
type MembershipTransactionStatus = 'Completed' | 'Failed' | 'Pending'

/**
 * What `membership/subscription-payments/transactions` actually returns: the
 * shared `SubscriptionPayment` resource, with the subscription tree the table
 * needs eager-loaded. No dedicated backend read-model resource.
 */
interface MembershipTransactionPayload extends SubscriptionPayment {
  subscription?: {
    id: number
    plan?: MembershipTransactionPayloadPlan
    coupon?: Coupon
    membershipProfiles?: MembershipProfile[]
  }
}

/**
 * The plan as this endpoint serializes it: the shared `Plan` resource plus the
 * lite `currency` embed it adds. Not on the `Plan` model itself - only these
 * analytics rows carry it, and widening `Plan` would imply every plan payload
 * has it.
 */
type MembershipTransactionPayloadPlan = Plan & {
  currency?: Pick<Currency, 'isoCode' | 'symbol'> | null
}

/**
 * The flat row the table, the preview and the SBRM view all read, mapped from
 * the payload by `mapPaymentToMembershipTransaction`. `member`/`plan` stay lite
 * shapes here, so they go through `LiteMemberAvatar` (which maps the embed onto
 * `MembershipProfile.Avatar`) and `LitePlanTag` rather than `Plan.Tag`'s store
 * lookup.
 */
// `SBModelUUID`, not `SBModel`: a payment is keyed by a UUID
// (`subscription_payments.id`), like the `SubscriptionPayment` model.
interface MembershipTransaction extends SBModelUUID {
  date: string | null
  reference: string
  /** Deep link to the payment within the PSP dashboard, when available. */
  pspUrl: string | null
  member: MembershipTransactionMember | null
  plan: MembershipTransactionPlan | null
  /** What this payment would have cost without its coupon, in cents - the
   * strikethrough source when it differs from `netAmount`. Comes from the
   * amount recorded on the payment itself, so it stays true even after the
   * plan's price or the coupon changes. Null on the payments taken before the
   * backend recorded it, where the column renders `--` rather than a price we
   * would have to guess. */
  listPrice: number | null
  /**
   * The whole coupon, not just its code: both surfaces render it with
   * `Coupon.Avatar` like the members table, which shows the discount amount and
   * keeps the code in its tooltip.
   */
  coupon: Coupon | null
  /** Amount actually paid, in cents. */
  netAmount: number
  /** Null on the payments made before the backend captured it. */
  paymentMethod: string | null
  status: MembershipTransactionStatus
}

/**
 * Settlement timestamps -> status label, same precedence as the
 * `filters[status][]` tokens the backend matches on: a completed payment wins
 * over a failed one, and neither timestamp means still pending.
 */
const membershipTransactionStatus = (
  payment: Pick<SubscriptionPayment, 'completedAt' | 'failedAt'>
): MembershipTransactionStatus => {
  if (payment.completedAt) {
    return 'Completed'
  }

  return payment.failedAt ? 'Failed' : 'Pending'
}

/** Flattens the shared payment payload into the row the tables render. */
const mapPaymentToMembershipTransaction = (
  payment: MembershipTransactionPayload
): MembershipTransaction => {
  const member = payment.subscription?.membershipProfiles?.[0]
  const plan = payment.subscription?.plan

  return {
    id: payment.id,
    date: payment.createdAt ?? null,
    reference: payment.pspReference,
    pspUrl: payment.pspUrl ?? null,
    member: member
      ? {
          id: member.id,
          firstName: member.firstName ?? '',
          lastName: member.lastName ?? '',
          email: member.email ?? '',
          avatar: member.profilePictureUrl ?? null,
        }
      : null,
    // `en.name`, like `Plan.Tag` - plan names are localized, never flat.
    plan: plan
      ? {
          id: plan.id,
          name: plan.en.name,
          avatar: null,
          currency: plan.currency ?? null,
        }
      : null,
    // The payment's own recorded gross, not `plan.price`: the plan's price today
    // is not what this member was charged against back then.
    listPrice: payment.amountBeforeDiscount ?? null,
    coupon: payment.subscription?.coupon ?? null,
    netAmount: payment.amount,
    paymentMethod: payment.paymentMethod ?? null,
    status: membershipTransactionStatus(payment),
  }
}

interface MembershipTransactionStatusMetaEntry {
  badge: 'success' | 'error' | 'processing'
  label: string
}

/** Backend status string -> Badge status/label, driving the Status column. */
const membershipTransactionStatusMeta: Record<
  MembershipTransactionStatus,
  MembershipTransactionStatusMetaEntry
> = {
  Completed: { badge: 'success', label: 'Completed' },
  Failed: { badge: 'error', label: 'Failed' },
  Pending: { badge: 'processing', label: 'Pending' },
}

export {
  mapPaymentToMembershipTransaction,
  membershipTransactionStatus,
  membershipTransactionStatusMeta,
}
export type {
  MembershipTransaction,
  MembershipTransactionMember,
  MembershipTransactionPayload,
  MembershipTransactionPlan,
  MembershipTransactionStatus,
}
